import React, { useEffect, useState } from "react";
import "./style.scss";
import ArrowIncrease from "../../../assets/icons/arrow-increase.svg";
import ArrowDecrease from "../../../assets/icons/arrow-decrease.svg";
import Loss from "../../../assets/images/loss-chart.svg";
import InfoBox from "../../../reusableComponents/InfoBox";
import TrophyIcon from "../../../assets/icons/trophy-white.svg";
import ContestantsIcon from "../../../assets/icons/people-white.svg";
import Gain from "../../../assets/images/gain-chart.svg";
import PushLeads, { Chart } from "./PushLeads";
import Visitor from "./Visitor";
import OverviewTable from "./OverviewTable";
import AreaPreviewChart from "./AreaPreviewChart";
import { useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading } from "../../../store/reducers/authSlice";
import {
  baseUrl,
  getAffiliateCodelist,
  getCodelistV2,
} from "../../../utils/api/apis";
import { Tooltip } from "react-tooltip";
import { returnErrors } from "../../../store/reducers/error";
import { getAffiliateDataSuccess } from "../../../store/reducers/affiliateSlice";
import Select from "react-select";
import { formatCurrency } from "../../../utils/helpers/string";

function Overview({ setActiveTab }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const idToken = useSelector((state) => state.auth.idToken);
  const affiliateId = searchParams.get("affiliateId");
  const email = searchParams.get("email");

  const dispatch = useDispatch();
  const [affiliateStats, setAffiliateStats] = useState({});
  const [tableData, setTableData] = useState([]);
  const [codeData, setCodeData] = useState(null);
  const [pushLeadData, setPushLeadData] = useState(null);
  const [activeCode, setActiveCode] = useState(null);
  const [isDropdownSelected, setIsDropdownSelected] = useState(false);

  const fetchAffiliateStats = async (affId) => {
    const targetAffiliateId = affId || affiliateId;
    if (!targetAffiliateId) return;

    dispatch(setIsLoading(true));
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
      };
      const response = await axios.get(
        `${baseUrl}affiliates/admin/affiliate-stats/?affiliate_id=${targetAffiliateId}`,
        config
      );
      setAffiliateStats(response?.data || {});
    } catch (error) {
      console.error("Error fetching affiliate stats:", error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const fetchTable = async () => {
    dispatch(setIsLoading(true));
    try {
      const currentEmail = email || searchParams.get("email");
      const response = await getCodelistV2(idToken, currentEmail);
      const results = response?.data?.results || [];
      setTableData(results);

      if (response?.status > 399) {
        dispatch(returnErrors("Error Fetching Affiliate List", 400));
      } else if (!affiliateId && results.length > 0) {
        const foundAffiliateId = results[0]?.affiliate_id || results[0]?.affiliate;
        if (foundAffiliateId) {
          fetchAffiliateStats(foundAffiliateId);
        }
      }
    } catch (error) {
      console.error("Error fetching code list:", error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  useEffect(() => {
    if (idToken) {
      fetchTable();
      if (affiliateId) {
        fetchAffiliateStats(affiliateId);
      }
    }
  }, [idToken, affiliateId]);

  const customStyles = {
    control: (provided) => ({
      ...provided,
      width: "100%",
      height: "36px",
      minHeight: "36px",
      boxSizing: "content-box",
      fontSize: "12px",
      color: "#133C55",
      fontWeight: "500",
      fontFamily: "Space Grotesk",
      borderRadius: "8px",
      float: "right",
      dropdownIndicator: (provided) => ({
        ...provided,
        color: "#133C55",
        width: "20px",
      }),
    }),
  };

  // code options for dropdown with "All Codes" option
  const codeOptions = React.useMemo(
    () => [
      { value: "all", label: "All Codes" },
      ...(tableData?.map((item) => ({
        value: item?.id,
        label: `${item?.code}`,
      })) || []),
    ],
    [tableData]
  );

  // Handle change for react select
  const handleChange = async (selectedOption) => {
    if (!selectedOption || selectedOption.value === "all") {
      setActiveCode(selectedOption?.value === "all" ? selectedOption : null);
      setIsDropdownSelected(false);
      setCodeData(null);
      setPushLeadData(null);
      return;
    }

    setIsDropdownSelected(true);
    dispatch(setIsLoading(true));
    setActiveCode(selectedOption);
    const codeId = selectedOption?.value;

    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
      };
      const [response, responseTwo] = await Promise.all([
        axios.get(
          `${baseUrl}affiliates/admin/affiliate-code-stats/?code_id=${codeId}`,
          config
        ),
        axios.get(
          `${baseUrl}affiliates/admin/affiliate-stats/?affiliate_code_id=${codeId}`,
          config
        ),
      ]);

      setCodeData(response?.data);
      setPushLeadData(responseTwo?.data);
    } catch (error) {
      console.error("Error fetching code specific stats:", error);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const isCodeSpecific = isDropdownSelected && activeCode && activeCode.value !== "all";
  const displayedStats = isCodeSpecific ? pushLeadData : affiliateStats;

  const infoBoxArr = [
    {
      title: "Conversion",
      value: displayedStats?.conversions ?? 0,
      icon: ArrowIncrease,
      color: "#F75C03",
    },
    {
      title: "Push leads",
      value: displayedStats?.pushed_leads ?? 0,
      icon: ArrowIncrease,
      color: "#4CB944",
    },
    {
      title: "Lifetime Comissions",
      value: formatCurrency(Number(displayedStats?.lifetime_commissions || 0)),
      icon: ArrowIncrease,
      color: "#1877F2",
    },
    {
      title: "Commissions Payable",
      value: formatCurrency(Number(displayedStats?.current_commissions || 0)),
      icon: ArrowIncrease,
      color: "#1877F2",
    },
    {
      title: "Commissions Paid",
      value: formatCurrency(Number(displayedStats?.approved_commissions || 0)),
      icon: ArrowIncrease,
      color: "#1877F2",
    },
    {
      title: "Total failed conversions",
      value: isCodeSpecific
        ? codeData?.total_failed_conversions ?? 0
        : displayedStats?.total_failed_conversions ?? "-",
      icon: ArrowIncrease,
      color: "#1877F2",
    },
    {
      title: "Total succeeded conversions",
      value: isCodeSpecific
        ? codeData?.total_succeeded_conversions ?? 0
        : displayedStats?.total_succeeded_conversions ?? "-",
      icon: ArrowIncrease,
      color: "#1877F2",
    },
  ];

  return (
    <div className="overview affiliate_list_details">
      <Tooltip id="my-tooltip" />

      <div className="grid_container">
        <div className="papercon multi">
          <div className="innerIcon">
            <div className="HeadT">Email</div>
            <div className="TextB">
              {email || tableData?.[0]?.email || "-"}
            </div>
          </div>
          <div className="innerIcon">
            <div className="HeadT">Total codes</div>
            <div className="TextB">{tableData?.length || 0}</div>
          </div>
          <div className="code_dropdown">
            <div className="HeadT">Code List</div>
            <Select
              options={codeOptions}
              styles={customStyles}
              onChange={handleChange}
              value={activeCode || { value: "all", label: "All Codes" }}
              placeholder="All Codes"
              isClearable={false}
            />
          </div>
        </div>

        <div className="column column-70">
          <div className="row-1">
            <div className="infobox_col1_container">
              {infoBoxArr?.map((info) => (
                <InfoBox
                  key={info.title}
                  title={info.title}
                  value={info.value}
                  icon={info.icon}
                  percentage={info.percentage}
                  color={info.color}
                  variant={1}
                />
              ))}
            </div>
          </div>
          <div className="box row-2 push_leads">
            <PushLeads data={displayedStats?.click_stats || []} />
          </div>
        </div>
      </div>

      {isCodeSpecific && codeData && (
        <div className="box table_container">
          <OverviewTable codeData={codeData} />
        </div>
      )}
    </div>
  );
}

export default Overview;
